import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Panel, Eyebrow, MonoLabel } from "@/components/Panel";

export default function BigScreenKioskSetup() {
  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/big-screen" className="btn-ghost" data-testid="kiosk-back-btn"><ArrowLeft size={14} /> Back to Big Screen</Link>
        <Eyebrow className="mt-6">AV Team · Kiosk Setup</Eyebrow>
        <h1 className="h-hero mt-3 text-4xl">One projector, one browser tab.</h1>
        <div className="mt-4 mono-label" style={{ color: "var(--taupe)" }}>
          Follow these steps once per venue display. Keep this page open on your laptop for reference.
        </div>

        <Panel className="p-6 mt-8" data-testid="kiosk-step-1">
          <MonoLabel>1 · Open Chrome (or Edge) in kiosk mode</MonoLabel>
          <div className="mt-3 text-sm space-y-2">
            <div>Windows: <code className="font-mono">chrome.exe --kiosk https://your.domain/big-screen?auto=1</code></div>
            <div>macOS: <code className="font-mono">"/Applications/Google Chrome.app/Contents/MacOS/Google Chrome" --kiosk https://your.domain/big-screen?auto=1</code></div>
            <div>Linux: <code className="font-mono">google-chrome --kiosk https://your.domain/big-screen?auto=1</code></div>
            <div className="mono-label" style={{ color: "var(--taupe)" }}>The <code>?auto=1</code> makes it request fullscreen on load.</div>
          </div>
        </Panel>

        <Panel className="p-6 mt-4" data-testid="kiosk-step-2">
          <MonoLabel>2 · Disable OS interruptions</MonoLabel>
          <ul className="mt-3 text-sm space-y-1 list-disc list-inside" style={{ color: "var(--body)" }}>
            <li>Turn OFF sleep, screen-lock, and screensaver in Power settings.</li>
            <li>Turn ON Do-Not-Disturb / Focus mode (no popups on the projected screen).</li>
            <li>Pause OS updates for the day (Windows: "Pause updates 1 week"; macOS: quit Software Update).</li>
            <li>Turn OFF Bluetooth pairing prompts.</li>
          </ul>
        </Panel>

        <Panel className="p-6 mt-4" data-testid="kiosk-step-3">
          <MonoLabel>3 · Chrome hygiene</MonoLabel>
          <ul className="mt-3 text-sm space-y-1 list-disc list-inside" style={{ color: "var(--body)" }}>
            <li>Disable auto-update reminders (<code>chrome://settings</code> → About Chrome, ignore restart prompts).</li>
            <li>Disable password-save popups (<code>chrome://settings/passwords</code>).</li>
            <li>Disable translate prompts (<code>chrome://settings/languages</code>).</li>
            <li>Close all other tabs. The big-screen tab must be the only one.</li>
          </ul>
        </Panel>

        <Panel className="p-6 mt-4" data-testid="kiosk-step-4">
          <MonoLabel>4 · Network resilience</MonoLabel>
          <ul className="mt-3 text-sm space-y-1 list-disc list-inside" style={{ color: "var(--body)" }}>
            <li>Prefer wired Ethernet over Wi-Fi.</li>
            <li>If the tab loses connection, the top bar shows a red "reconnecting…" chip and retries automatically (up to 30s backoff).</li>
            <li>Keep the venue router visible to the AV desk in case of restart.</li>
          </ul>
        </Panel>

        <Panel className="p-6 mt-4" data-testid="kiosk-step-5">
          <MonoLabel>5 · Test before doors open</MonoLabel>
          <ul className="mt-3 text-sm space-y-1 list-disc list-inside" style={{ color: "var(--body)" }}>
            <li>Ask admin to fire an announcement (Admin → Big Screen → Announce "Test — please ignore" · TTL 15s).</li>
            <li>Log a dummy referral from any leader tablet; watch the feed animate top-in within a second.</li>
            <li>Confirm at both 1080p and 4K if you have multiple projectors.</li>
          </ul>
        </Panel>

        <div className="mt-8 mono-label" style={{ color: "var(--taupe)" }}>
          Questions? Ping the tech lead on the ops WhatsApp group before 09:00 IST.
        </div>
      </div>
    </div>
  );
}
